import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, AlertTriangle, ArrowLeft, Send, Package, ShieldCheck, ScanLine } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import PhotoUpload from "@/components/PhotoUpload";
import QrScanner from "@/components/QrScanner";
import { queueExecution, isNetworkError } from "@/lib/offline";

export function ChecklistRunnerBody({ executionId, onExit, onLoaded }) {
  const id = executionId;
  const navigate = useNavigate();
  const [execution, setExecution] = useState(null);
  const [template, setTemplate] = useState(null);
  const [answers, setAnswers] = useState({});
  const [aiVerdicts, setAiVerdicts] = useState({}); // qid -> verdict
  const [stockConsumption, setStockConsumption] = useState({}); // qid -> {item_id: qty}
  const [stockItemsMap, setStockItemsMap] = useState({}); // item_id -> item
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: exec } = await api.get(`/checklists/executions/${id}`);
        setExecution(exec);
        const { data: tpl } = await api.get(
          `/checklists/templates/${exec.template_id}`
        );
        setTemplate(tpl);
        if (onLoaded) onLoaded(tpl, exec);

        // Preload stock items map if any question is stock_control
        const needsStock = (tpl.questions || []).some(
          (q) => q.type === "stock_control"
        );
        if (needsStock) {
          try {
            const { data: products } = await api.get("/sanitary/products?include_empty=true");
            const map = {};
            for (const p of products) {
              map[p.id] = { ...p, is_product: true };
              for (const lot of p.lots || []) map[lot.id] = { ...lot, product_name: p.name };
            }
            setStockItemsMap(map);
          } catch (err) {
            console.error("Failed to load stock products", err);
          }
        }

        if (exec.status === "completed") {
          setCompleted({
            compliance_rate: exec.compliance_rate,
            non_compliant_count: exec.non_compliant_count,
          });
          const initial = {};
          const initialVerdicts = {};
          const initialStock = {};
          for (const r of exec.responses || []) {
            initial[r.question_id] = r.value;
            if (r.ai_verdict) initialVerdicts[r.question_id] = r.ai_verdict;
            if (r.stock_consumption?.length) {
              initialStock[r.question_id] = Object.fromEntries(
                r.stock_consumption.map((e) => [e.item_id, e.quantity])
              );
            }
          }
          setAnswers(initial);
          setAiVerdicts(initialVerdicts);
          setStockConsumption(initialStock);
          setNotes(exec.notes || "");
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Falha ao carregar execução");
        if (onExit) onExit(false);
        else navigate("/executions");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  const setAnswer = (qid, value) => setAnswers((a) => ({ ...a, [qid]: value }));
  const setVerdict = (qid, verdict) =>
    setAiVerdicts((a) => ({ ...a, [qid]: verdict }));
  const [stockScanned, setStockScanned] = useState({}); // item_id -> true (lido pela etiqueta QR)
  const setStockQty = (qid, itemId, qty, scanned = false) => {
    setStockConsumption((s) => ({
      ...s,
      [qid]: { ...(s[qid] || {}), [itemId]: qty },
    }));
    if (scanned) setStockScanned((m) => ({ ...m, [itemId]: true }));
  };

  // Pre-submit validation:
  // - photo questions with require_ai_validation must have a verdict
  // - FASE 20: required questions must be answered before submitting
  const validationError = useMemo(() => {
    for (const q of template?.questions || []) {
      if (q.type === "photo" && q.require_ai_validation !== false) {
        if (!answers[q.id]) {
          if (q.required) return `Envie e valide com IA a foto de "${q.label}"`;
          continue;
        }
        if (!aiVerdicts[q.id]) {
          return `Clique em "Validar com IA" para a foto de "${q.label}" antes de finalizar`;
        }
        continue;
      }
      if (!q.required) continue;
      if (q.type === "photo") {
        if (!answers[q.id]) return `Tire a foto de "${q.label}" — resposta obrigatória`;
      } else if (q.type === "stock_control") {
        const entries = Object.values(stockConsumption[q.id] || {}).filter(
          (v) => Number(v) > 0
        );
        if (entries.length === 0)
          return `Informe ao menos um consumo em "${q.label}" — resposta obrigatória`;
      } else if (
        answers[q.id] === undefined ||
        answers[q.id] === null ||
        String(answers[q.id]).trim() === ""
      ) {
        return `Responda "${q.label}" — resposta obrigatória`;
      }
    }
    return null;
  }, [template, answers, aiVerdicts, stockConsumption]);

  // FASE 20 — geolocation stamp (best-effort, 6s timeout)
  const getPosition = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 6000, maximumAge: 60000 }
      );
    });

  const submit = async () => {
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setSubmitting(true);
    const responses = (template.questions || []).map((q) => {
      const base = {
        question_id: q.id,
        value: String(answers[q.id] ?? ""),
        photo_url: q.type === "photo" ? String(answers[q.id] || "") : "",
        compliant: true,
        notes: "",
        ai_verdict: q.type === "photo" ? aiVerdicts[q.id] || null : null,
        stock_consumption: [],
      };
      if (q.type === "stock_control") {
        const entries = Object.entries(stockConsumption[q.id] || {})
          .map(([item_id, qty]) => ({
            item_id,
            quantity: Number(qty) || 0,
            scanned: !!stockScanned[item_id],
          }))
          .filter((e) => e.quantity > 0);
        base.stock_consumption = entries;
        base.value = entries.length ? `${entries.length} item(ns)` : "";
      }
      return base;
    });
    const geo = await getPosition();
    const payloadBody = {
      responses,
      notes,
      ...(geo ? { latitude: geo.latitude, longitude: geo.longitude } : {}),
    };
    try {
      const { data } = await api.post(
        `/checklists/executions/${id}/complete`,
        payloadBody
      );
      let msg = data.non_compliant_count > 0
        ? `Concluído com ${data.non_compliant_count} não conformidade(s)`
        : "Checklist concluído sem desvios!";
      if (data.pending_consumptions > 0) {
        msg += ` · ${data.pending_consumptions} consumo(s) aguardando aprovação`;
      }
      toast.success(msg);
      // FASE 17.6 — return to the dashboard; the checklist leaves the pending
      // list and remains available in "Execuções".
      if (onExit) onExit(true);
      else navigate("/dashboard");
    } catch (err) {
      // FASE 14 — offline: queue the submission and sync when back online.
      if (isNetworkError(err)) {
        queueExecution({
          executionId: id,
          payload: payloadBody,
          templateName: template?.name,
        });
        toast.success(
          "Sem conexão: checklist salvo no dispositivo e será enviado automaticamente quando a internet voltar.",
          { duration: 6000 }
        );
        if (onExit) onExit(true);
        else navigate("/dashboard");
      } else {
        toast.error(err?.response?.data?.detail || "Falha ao finalizar");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!template || !execution) {
    return <div className="text-stone-500 font-body">Aguarde...</div>;
  }

  const isReadOnly = !!completed;

  return (
      <div data-testid="checklist-runner" className="max-w-4xl space-y-4">
        {completed && (
          <Card
            className={`p-6 rounded-2xl border-2 ${
              completed.non_compliant_count > 0
                ? "border-amber-300 bg-amber-50/50"
                : "border-emerald-300 bg-emerald-50/50"
            }`}
            data-testid="runner-result"
          >
            <div className="flex items-center gap-3">
              {completed.non_compliant_count > 0 ? (
                <AlertTriangle className="h-7 w-7 text-amber-700" />
              ) : (
                <CheckCircle2 className="h-7 w-7 text-emerald-700" />
              )}
              <div>
                <div className="font-display text-2xl font-bold tracking-tight">
                  Conformidade: {completed.compliance_rate?.toFixed(1)}%
                </div>
                <div className="text-sm text-stone-600 font-body">
                  {completed.non_compliant_count > 0
                    ? `${completed.non_compliant_count} item(ns) fora do padrão · ocorrência criada automaticamente`
                    : "Nenhum desvio identificado"}
                </div>
                {/* FASE 20 — geofence stamp */}
                {completed.geo_out_of_range === true && (
                  <Badge className="mt-2 bg-rose-600 text-white border-none" data-testid="runner-geo-out">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Fora do local da loja ({Math.round(completed.geo_distance_m || 0)}m de distância)
                  </Badge>
                )}
                {completed.geo_out_of_range === false && (
                  <Badge className="mt-2 bg-emerald-700 text-white border-none" data-testid="runner-geo-ok">
                    <CheckCircle2 className="h-3 w-3 mr-1" /> Executado no local da loja
                  </Badge>
                )}
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <Button variant="outline" onClick={() => navigate("/executions")}>
                Ver execuções
              </Button>
              {completed.non_compliant_count > 0 && (
                <Button
                  onClick={() => navigate("/occurrences")}
                  className="bg-amber-700 hover:bg-amber-800 text-white"
                >
                  Ver ocorrências
                </Button>
              )}
            </div>
          </Card>
        )}

        {template.questions?.map((q, idx) => (
          <Card key={q.id} className="p-5 rounded-xl border-stone-200 bg-white" data-testid={`runner-q-${idx}`}>
            <div className="flex items-start gap-3">
              <div className="h-7 w-7 rounded-md bg-emerald-100 text-emerald-800 flex items-center justify-center font-display font-bold text-sm shrink-0">
                {idx + 1}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Label className="font-body font-semibold text-stone-900">
                    {q.label} {q.required && <span className="text-rose-600">*</span>}
                  </Label>
                  <Badge variant="outline" className="border-stone-300">{q.type}</Badge>
                  {q.type === "photo" && q.require_ai_validation !== false && (
                    <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-800 text-[10px]">
                      <ShieldCheck className="h-3 w-3 mr-1" /> IA obrigatória
                    </Badge>
                  )}
                </div>
                {q.help_text && (
                  <div className="text-xs text-stone-500 font-body mt-0.5">{q.help_text}</div>
                )}
                <div className="mt-3">
                  {renderInput({
                    q,
                    idx,
                    value: answers[q.id] ?? "",
                    onChange: (v) => setAnswer(q.id, v),
                    readOnly: isReadOnly,
                    onAiVerdict: (v) => setVerdict(q.id, v),
                    aiVerdict: aiVerdicts[q.id],
                    stockValues: stockConsumption[q.id] || {},
                    onStockQty: (itemId, qty, scanned) => setStockQty(q.id, itemId, qty, scanned),
                    stockItemsMap,
                  })}
                </div>
                {(q.type === "number" || q.type === "temperature") &&
                  (q.min_value != null || q.max_value != null) && (
                    <div className="text-xs text-stone-500 font-body mt-1.5">
                      Faixa conforme: {q.min_value ?? "—"} a {q.max_value ?? "—"}
                    </div>
                  )}
              </div>
            </div>
          </Card>
        ))}

        {/* Observações gerais — visible both when filling and when read-only (16.8c) */}
        <Card className="p-5 rounded-xl border-stone-200 bg-white">
          <Label className="font-body text-sm">
            Observações gerais
            {isReadOnly && (
              <Badge variant="outline" className="ml-2 text-[10px] border-stone-300">
                registro do executor
              </Badge>
            )}
          </Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="mt-1 bg-white"
            disabled={isReadOnly}
            placeholder={isReadOnly ? "(sem observações registradas)" : "Descreva algum contexto adicional para o admin…"}
            data-testid="runner-notes"
          />
          {!isReadOnly && (
            <>
              {validationError && (
                <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-body text-amber-900">
                  <AlertTriangle className="h-3.5 w-3.5 inline-block mr-1" />
                  {validationError}
                </div>
              )}
              <Button
                onClick={submit}
                disabled={submitting}
                className="mt-4 bg-emerald-800 hover:bg-emerald-900 text-white h-11 px-6 font-body"
                data-testid="runner-submit"
              >
                <Send className="h-4 w-4 mr-2" />
                {submitting ? "Enviando..." : "Finalizar checklist"}
              </Button>
            </>
          )}
        </Card>
      </div>
  );
}

export default function ChecklistRunner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  return (
    <Layout
      title={meta?.name || "Checklist"}
      subtitle={
        meta ? `Execução ${meta.execId.slice(0, 8)} · ${meta.count} pergunta(s)` : "Carregando..."
      }
      actions={
        <Button variant="outline" onClick={() => navigate("/executions")} data-testid="runner-back">
          <ArrowLeft className="h-4 w-4 mr-2" /> Execuções
        </Button>
      }
    >
      <ChecklistRunnerBody
        executionId={id}
        onLoaded={(tpl, exec) =>
          setMeta({ name: tpl.name, execId: exec.id, count: tpl.questions?.length || 0 })
        }
      />
    </Layout>
  );
}

function renderInput({
  q,
  idx,
  value,
  onChange,
  readOnly,
  onAiVerdict,
  aiVerdict,
  stockValues,
  onStockQty,
  stockItemsMap,
}) {
  const tid = `runner-input-${idx}`;
  const common = { disabled: readOnly, "data-testid": tid };
  switch (q.type) {
    case "number":
    case "temperature":
      return (
        <Input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="bg-white"
          {...common}
        />
      );
    case "yes_no":
      return (
        <Select value={value || ""} onValueChange={onChange} disabled={readOnly}>
          <SelectTrigger className="bg-white" data-testid={tid}>
            <SelectValue placeholder="Selecione" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="yes">Sim</SelectItem>
            <SelectItem value="no">Não</SelectItem>
          </SelectContent>
        </Select>
      );
    case "multiple_choice":
      return (
        <Select value={value || ""} onValueChange={onChange} disabled={readOnly}>
          <SelectTrigger className="bg-white" data-testid={tid}>
            <SelectValue placeholder="Selecione" />
          </SelectTrigger>
          <SelectContent>
            {(q.options || []).map((opt) => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case "photo":
      return (
        <PhotoUpload
          value={value}
          onChange={(v) => {
            onChange(v);
            onAiVerdict(null); // reset verdict when photo changes
          }}
          disabled={readOnly}
          testId={tid}
          aiContext={q.label || q.help_text || ""}
          onVerdict={onAiVerdict}
          initialVerdict={aiVerdict}
          referencePhotoUrl={q.reference_photo_url || ""}
        />
      );
    case "signature":
      return (
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Nome / matrícula do responsável"
          className="bg-white"
          {...common}
        />
      );
    case "qr_code":
      return (
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Código lido"
          className="bg-white"
          {...common}
        />
      );
    case "stock_control":
      return (
        <StockControlField
          q={q}
          tid={tid}
          readOnly={readOnly}
          stockValues={stockValues}
          onStockQty={onStockQty}
          stockItemsMap={stockItemsMap}
        />
      );
    default:
      return (
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="bg-white"
          {...common}
        />
      );
  }
}

// Converte o peso por unidade (g/kg/ml/L) para a unidade do estoque do item
function unitWeightInStockUnit(it) {
  const w = Number(it?.unit_weight);
  if (!w || w <= 0) return null;
  const from = (it.unit_weight_unit || "g").toLowerCase();
  const to = (it.unit || "").toLowerCase();
  const mass = { g: 1, kg: 1000 };
  const vol = { ml: 1, l: 1000 };
  let v = w;
  if (mass[from] != null && mass[to] != null) v = (w * mass[from]) / mass[to];
  else if (vol[from] != null && vol[to] != null) v = (w * vol[from]) / vol[to];
  return Math.round(v * 1000) / 1000;
}

// Controle de Estoque — campo de baixa/entrada com leitura de QR Code da etiqueta
function StockControlField({ q, tid, readOnly, stockValues, onStockQty, stockItemsMap }) {
  const [scanOpen, setScanOpen] = useState(false);
  const ids = q.stock_item_ids || [];
  const op = q.stock_operation === "in" ? "in" : "out";
  // itens configurados + itens adicionados via QR
  const shownIds = Array.from(new Set([...ids, ...Object.keys(stockValues || {})]));
  const fmtBR = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "sem validade");

  // Produto de um item do mapa (o item pode ser o próprio produto ou um lote)
  const productOf = (it) => (it?.is_product ? it : it?.product_id ? stockItemsMap[it.product_id] : null);
  // Lotes com saldo em ordem PVPS (vence primeiro, sai primeiro)
  const pvpsLots = (product) =>
    (product?.lots || [])
      .filter((l) => Number(l.quantity) > 0)
      .sort((a, b) => (a.expires_at == null) - (b.expires_at == null) || String(a.expires_at || "").localeCompare(String(b.expires_at || "")));
  const [pvpsWarnings, setPvpsWarnings] = useState({}); // lotId -> lote esperado

  const handleDecode = (text) => {
    let itemId = null;
    let qty = null;
    try {
      const data = JSON.parse(text);
      if (data && data.t === "ucstock" && data.id) {
        itemId = data.id;
        qty = data.q;
      }
    } catch {
      if (typeof text === "string" && text.trim().length >= 8) itemId = text.trim();
    }
    const it = itemId ? stockItemsMap[itemId] : null;
    if (!it) {
      toast.error("QR Code não corresponde a um lote do estoque");
      return;
    }
    if (op === "out") {
      // PVPS: a etiqueta lida deve ser do lote que vence primeiro
      const product = productOf(it);
      const lots = pvpsLots(product);
      if (!it.is_product && lots.length > 1 && lots[0].id !== it.id) {
        const exp = lots[0];
        setPvpsWarnings((w) => ({ ...w, [it.id]: exp }));
        toast.warning(
          `Fora da ordem PVPS: este lote vence em ${fmtBR(it.expires_at)}, mas o lote ${exp.batch || "s/ nº"} vence antes (${fmtBR(exp.expires_at)}). Uma ocorrência será registrada.`,
          { duration: 7000 }
        );
      }
      // Baixa: cada leitura = 1 unidade consumida (peso por unidade), acumulando
      const step = unitWeightInStockUnit(it) ?? 1;
      const current = Number(stockValues?.[itemId]) || 0;
      const total = Math.round((current + step) * 1000) / 1000;
      onStockQty(itemId, total, true);
      toast.success(
        `${it.name} — baixa de ${step} ${it.unit || ""}${current > 0 ? ` (total: ${total} ${it.unit || ""})` : ""} adicionada (ajuste se necessário)`
      );
    } else {
      const prefill = Number(qty ?? it.lot_quantity ?? it.quantity) || 1;
      onStockQty(itemId, prefill, true);
      toast.success(
        `${it.name} — entrada de ${prefill} ${it.unit || ""} adicionada (ajuste se necessário)`
      );
    }
  };

  if (shownIds.length === 0) {
    return (
      <div className="text-xs text-stone-500 font-body italic">
        Configuração inválida: nenhum item associado.
      </div>
    );
  }
  const tone =
    op === "in" ? "border-emerald-200 bg-emerald-50/40" : "border-rose-200 bg-rose-50/40";
  const label = op === "in" ? "Entrada — soma ao saldo" : "Baixa — subtrai do saldo";
  return (
    <div className="space-y-2" data-testid={tid}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div
          className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] font-body ${tone}`}
        >
          <Package className="h-3 w-3" /> <span className="font-semibold">{label}</span>
        </div>
        {!readOnly && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="bg-white border-emerald-300 text-emerald-900 hover:bg-emerald-50"
            onClick={() => setScanOpen(true)}
            data-testid={`${tid}-scan-btn`}
          >
            <ScanLine className="h-4 w-4 mr-1.5" /> Ler QR Code
          </Button>
        )}
      </div>
      {shownIds.map((iid) => {
        const it = stockItemsMap[iid];
        const product = productOf(it);
        const warn = pvpsWarnings[iid];
        return (
          <div
            key={iid}
            className={`flex items-center gap-3 border rounded-md px-3 py-2 ${warn ? "border-amber-300 bg-amber-50/60" : "border-stone-200 bg-stone-50/50"}`}
            data-testid={`${tid}-item-${iid}`}
          >
            <Package className="h-4 w-4 text-emerald-800 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-body text-sm font-semibold truncate">
                {it?.name || iid.slice(0, 8)}
                {it && !it.is_product && (
                  <Badge variant="outline" className="ml-2 text-[9px] border-emerald-300 text-emerald-800">
                    {it.batch ? `lote ${it.batch}` : "lote"} · val. {fmtBR(it.expires_at)}
                  </Badge>
                )}
                {warn && (
                  <Badge className="ml-2 text-[9px] bg-amber-200 text-amber-900 border-none" data-testid={`${tid}-pvps-warn-${iid}`}>
                    fora da ordem PVPS
                  </Badge>
                )}
              </div>
              <div className="text-[11px] text-stone-500 font-body">
                {product ? (
                  <>
                    Saldo total: {product.quantity} {product.unit || ""}
                    {product.lots_count > 1 && <span className="text-emerald-700"> · {product.lots_count} lotes (PVPS)</span>}
                    {warn && <span className="text-amber-800"> · use antes o lote {warn.batch || "s/ nº"} (val. {fmtBR(warn.expires_at)})</span>}
                  </>
                ) : (
                  <>Saldo: {it?.quantity ?? "—"} {it?.unit ?? ""}</>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Input
                type="number"
                step="0.01"
                min="0"
                value={stockValues[iid] ?? ""}
                onChange={(e) => onStockQty(iid, e.target.value)}
                disabled={readOnly}
                className="w-24 bg-white"
                placeholder="qtd"
                data-testid={`${tid}-qty-${iid}`}
              />
              <span className="text-xs text-stone-500 font-body">{it?.unit || ""}</span>
            </div>
          </div>
        );
      })}
      {!readOnly && (
        <p className="text-[11px] text-stone-500 font-body italic">
          {op === "in"
            ? "A soma no estoque só ocorre após aprovação do revisor."
            : "A subtração do estoque só ocorre após aprovação do revisor."}
        </p>
      )}
      <QrScanner
        open={scanOpen}
        onOpenChange={setScanOpen}
        onDecode={handleDecode}
        title="Ler etiqueta do produto"
      />
    </div>
  );
}
